import { useEffect, useState, useRef, useMemo } from "react";
import {
  BookOpen, ArrowUp, ArrowDown, Eye, EyeOff, Printer, Pencil, Save,
  Languages, Sparkles, Camera, Loader2, QrCode, Copy, RefreshCw, Flame, ChefHat, Leaf, Wine, Check, ExternalLink,
  Send, Palette, Smartphone, Tablet, Monitor
} from "lucide-react";
import PageHero from "../components/PageHero";
import OrnamentSVG from "../components/OrnamentSVG";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { eur, pct, ALLERGENS, ALLERGEN_BY_CODE } from "../lib/format";
import MenuFontPicker from "../components/MenuFontPicker";

// Charge les polices exposées à l'éditeur pour que les aperçus WYSIWYG
// soient visibles dans le picker (Chrome n'applique pas font-family sur
// les <option> natifs). Une seule requête Google Fonts par session.
function loadEditorFonts(families) {
  const uniq = Array.from(new Set((families || []).filter(Boolean)));
  if (!uniq.length) return;
  const id = "editor-page-fonts-" + uniq.join("|").replace(/\s+/g, "+");
  if (document.getElementById(id)) return;
  const fams = uniq.map((f) => `family=${encodeURIComponent(f)}:wght@400;600;700`).join("&");
  const link = document.createElement("link");
  link.id = id; link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${fams}&display=swap`;
  document.head.appendChild(link);
}

const LANGS = [
  { code: "fr", label: "Français",  flag: "🇫🇷" },
  { code: "en", label: "English",   flag: "🇬🇧" },
  { code: "es", label: "Español",   flag: "🇪🇸" },
  { code: "it", label: "Italiano",  flag: "🇮🇹" },
  { code: "de", label: "Deutsch",   flag: "🇩🇪" },
  { code: "zh", label: "中文",       flag: "🇨🇳" },
  { code: "ja", label: "日本語",     flag: "🇯🇵" },
];
const TR_LANGS = LANGS.filter((l) => l.code !== "fr");
const TR_CODES = TR_LANGS.map((l) => l.code);

const empty_item_tr = () => Object.fromEntries(TR_CODES.map((c) => [c, { display_name: "", description: "" }]));
const empty_header_tr = () => Object.fromEntries(TR_CODES.map((c) => [c, { title: "", subtitle: "", footer: "" }]));
const empty_ds_tr = () => Object.fromEntries(TR_CODES.map((c) => [c, { title: "", description: "" }]));

const DS_ACCENTS = [
  { code: "amber", label: "Ambre", ring: "ring-amber-300", dot: "bg-amber-400" },
  { code: "emerald", label: "Marge", ring: "ring-[#B4D1F9]", dot: "bg-[#24845B]" },
  { code: "rose", label: "Rose", ring: "ring-rose-300", dot: "bg-rose-400" },
  { code: "sky", label: "Ciel", ring: "ring-sky-300", dot: "bg-sky-400" },
  { code: "slate", label: "Ardoise", ring: "ring-slate-400", dot: "bg-slate-700" },
];
const DS_ICONS = [
  { code: "sparkles", label: "Étincelles", Icon: Sparkles },
  { code: "flame", label: "Flamme", Icon: Flame },
  { code: "chef", label: "Chef", Icon: ChefHat },
  { code: "leaf", label: "Feuille", Icon: Leaf },
  { code: "wine", label: "Verre", Icon: Wine },
];
const ICON_BY = Object.fromEntries(DS_ICONS.map(({ code, Icon }) => [code, Icon]));
const ACCENT_BAND = {
  amber: "from-amber-50 to-white ring-amber-200/70",
  emerald: "from-[#DCEBFF] to-white ring-[#DCEBFF]/70",
  rose: "from-rose-50 to-white ring-rose-200/70",
  sky: "from-sky-50 to-white ring-sky-200/70",
  slate: "from-slate-50 to-white ring-slate-200/70",
};

// Éditeur thème — polices exposées à l'utilisateur
const THEME_FONTS = [
  "Playfair Display", "Inter", "Manrope", "DM Serif Display", "Merriweather",
  "Bricolage Grotesque", "Cormorant Garamond", "Space Grotesk", "Lora",
];
const DEFAULT_THEME = {
  colors: { text_main: "#0F172A", text_secondary: "#5E6875", background: "#FAFAF7", accent: "#0F172A", titles: "#0F172A", separators: "#E2E8F0", ornament: "#B08D57" },
  fonts: { titles: "Playfair Display", dish: "Inter", description: "Inter", price: "Inter" },
  background: { type: "solid", value: "#FAFAF7", opacity: 1, intensity: 0.5 },
  decorations: { borders: true, separators: true, ornaments: false, framed_titles: false, price_dotted: true, logo_position: "top-center", item_spacing: 16 },
  ornaments: { title_frame: "none", text_frame: "none", underline: "none", frieze: "none", corner: "none" },
  show_descriptions: true, show_allergens: true, show_photos: true,
  font_size: 15, font_weight: 400, align: "left", line_height: 1.5,
};

// ---------------------------------------------------------------------------
// Bibliothèque d'ornements — 5 catégories × 5 styles + « Aucun ».
// Chaque style est une petite chaîne SVG paramétrée par la couleur courante
// (var(--rs-ornament)). Elle est rendue en vignette dans l'éditeur ET dans
// l'aperçu client via `<OrnamentSVG kind="..." style="..." />`.
// Styles adaptés brasserie / bistrot / cocktails / gastro / minimaliste.
// ---------------------------------------------------------------------------
export const ORNAMENT_LIBRARY = {
  title_frame: {
    label: "Cadres de titres",
    items: [
      { k: "none",       label: "Aucun" },
      { k: "brasserie",  label: "Brasserie" },
      { k: "bistrot",    label: "Bistrot" },
      { k: "cocktail",   label: "Cocktail" },
      { k: "gastro",     label: "Gastro" },
      { k: "minimal",    label: "Minimal" },
    ],
  },
  text_frame: {
    label: "Cadres de textes",
    items: [
      { k: "none",       label: "Aucun" },
      { k: "brasserie",  label: "Brasserie" },
      { k: "bistrot",    label: "Bistrot" },
      { k: "cocktail",   label: "Cocktail" },
      { k: "gastro",     label: "Gastro" },
      { k: "minimal",    label: "Minimal" },
    ],
  },
  underline: {
    label: "Soulignements",
    items: [
      { k: "none",       label: "Aucun" },
      { k: "brasserie",  label: "Traits" },
      { k: "bistrot",    label: "Losange" },
      { k: "cocktail",   label: "Étoile" },
      { k: "gastro",     label: "Point-tiret" },
      { k: "minimal",    label: "Ligne fine" },
    ],
  },
  frieze: {
    label: "Frises et séparateurs",
    items: [
      { k: "none",       label: "Aucun" },
      { k: "brasserie",  label: "Rosace" },
      { k: "bistrot",    label: "Chevron" },
      { k: "cocktail",   label: "Feuillage" },
      { k: "gastro",     label: "Fleur" },
      { k: "minimal",    label: "Points" },
    ],
  },
  corner: {
    label: "Ornements de coins",
    items: [
      { k: "none",       label: "Aucun" },
      { k: "brasserie",  label: "Angle épais" },
      { k: "bistrot",    label: "Ficelle" },
      { k: "cocktail",   label: "Feuille" },
      { k: "gastro",     label: "Arabesque" },
      { k: "minimal",    label: "Trait" },
    ],
  },
};

// Détecte les couleurs très proches — avertit l'utilisateur si le texte
// risque d'être illisible sur le fond choisi (WCAG basique).
function hexLuminance(hex) {
  if (!hex || !hex.startsWith("#")) return 0.5;
  const h = hex.replace("#", "");
  const parts = h.length === 3 ? [h[0]+h[0], h[1]+h[1], h[2]+h[2]] : [h.slice(0,2), h.slice(2,4), h.slice(4,6)];
  const [r, g, b] = parts.map((p) => parseInt(p, 16) / 255);
  const lin = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
function contrastRatio(a, b) {
  const l1 = hexLuminance(a);
  const l2 = hexLuminance(b);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/** Panneau d'édition visuelle du menu — couleurs, polices, fond, décorations. */
function ThemePanel({ theme, setTheme, previewDevice, setPreviewDevice, onPublish, publishing, publishProgress, translationsAllowed, published, translationsStale, canUndo, canRedo, onUndo, onRedo }) {
  const t = theme || DEFAULT_THEME;
  const patch = (path, value) => {
    const next = { ...t };
    const segs = path.split(".");
    let cur = next;
    for (let i = 0; i < segs.length - 1; i++) { cur[segs[i]] = { ...cur[segs[i]] }; cur = cur[segs[i]]; }
    cur[segs[segs.length - 1]] = value;
    setTheme(next);
  };
  const reset = () => setTheme(JSON.parse(JSON.stringify(DEFAULT_THEME)));
  const cr = contrastRatio(t.colors.text_main, t.colors.background);
  const contrastOk = cr >= 4.5;

  return (
    <Card className="card-soft border-0 no-print" data-testid="menu-theme-panel">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Palette className="w-4 h-4 text-[#FF9F1C]" /> Personnalisation visuelle
          </h3>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-start sm:justify-end">
            {/* Aperçu multi-device */}
            <div className="flex items-center rounded-lg border border-slate-200 bg-white p-0.5" role="group" aria-label="Aperçu">
              {[
                { k: "phone", Icon: Smartphone, label: "Téléphone" },
                { k: "tablet", Icon: Tablet, label: "Tablette" },
                { k: "desktop", Icon: Monitor, label: "Ordinateur" },
                { k: "print", Icon: Printer, label: "Impression" },
              ].map(({ k, Icon, label }) => (
                <button
                  key={k}
                  data-testid={`menu-preview-device-${k}`}
                  onClick={() => setPreviewDevice(k)}
                  title={label}
                  className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${previewDevice === k ? "bg-[#0B1F33] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={onUndo} disabled={!canUndo} data-testid="menu-theme-undo" title="Annuler (Ctrl+Z)">↶ Annuler</Button>
            <Button variant="outline" size="sm" onClick={onRedo} disabled={!canRedo} data-testid="menu-theme-redo" title="Rétablir (Ctrl+Y)">↷ Rétablir</Button>
            <Button variant="outline" size="sm" onClick={reset} data-testid="menu-theme-reset">Réinitialiser</Button>
            {translationsAllowed ? (
              <Button
                data-testid="menu-publish-btn"
                onClick={onPublish}
                disabled={publishing}
                title="Traduit et publie le menu dans les 7 langues"
                className="gap-2 h-9 px-4 rounded-lg font-semibold text-white disabled:opacity-50 bg-[#0B1F33] hover:bg-[#0F2A45] w-full sm:w-auto"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {publishing ? "Traduction en cours…" : "Traduire le menu"}
              </Button>
            ) : (
              <Button
                data-testid="menu-publish-btn-locked"
                onClick={() => toast.info("Traduction en 7 langues disponible avec la formule Intégral", { description: "Passez à Intégral pour traduire votre menu automatiquement.", action: { label: "Voir Intégral", onClick: () => { window.location.href = "/parametres"; } } })}
                title="Traduction en 7 langues disponible avec la formule Intégral"
                className="gap-2 h-9 px-4 rounded-lg font-semibold text-white bg-[#5E6875] hover:bg-[#5E6875] cursor-not-allowed w-full sm:w-auto"
              >
                <span aria-hidden>🔒</span>
                Traduire le menu
              </Button>
            )}
          </div>
        </div>

        {/* Progress publication */}
        {publishProgress && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px]" data-testid="menu-publish-progress">            <div className="font-semibold text-slate-800 mb-1">
              {publishProgress.status === "completed" ? "Les 7 langues sont prêtes · Menu publié" : publishProgress.status === "partial" ? "Publication partielle" : publishProgress.status === "failed" ? "Échec de publication" : "Préparation des 7 langues…"}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(publishProgress.target_languages || []).map((code) => {
                const done = (publishProgress.done || []).includes(code);
                const failed = !!(publishProgress.failed || {})[code];
                const current = publishProgress.current === code;
                return (
                  <span
                    key={code}
                    className={`px-2 py-0.5 rounded-full text-[11px] font-mono-num font-semibold ${
                      failed ? "bg-red-100 text-red-700"
                      : done ? "bg-emerald-100 text-emerald-800"
                      : current ? "bg-amber-100 text-amber-800 animate-pulse"
                      : "bg-white border border-slate-200 text-slate-500"
                    }`}
                  >
                    {code.toUpperCase()}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Bandeau « Traductions à actualiser » — apparaît dès qu'un
            produit / une recette a changé les allergènes ou le nom depuis
            la dernière publication multilingue. Se réinitialise après
            republication. Silencieux pour Essentiel / Pro (translations
            désactivées). */}
        {translationsAllowed && published && translationsStale && (
          <div
            className="rounded-md border border-[#F1D9A8] bg-[#FFF7E9] text-[#7A4B00] px-3 py-2 text-[12px] font-semibold flex items-center gap-2 flex-wrap"
            data-testid="menu-translations-stale-banner"
          >
            <span aria-hidden>⚠</span>
            Les traductions ne reflètent plus le menu actuel (allergènes ou textes modifiés).
            <button
              type="button"
              onClick={onPublish}
              disabled={publishing}
              data-testid="menu-translations-stale-republish"
              className="ml-auto underline decoration-2 underline-offset-2 hover:text-[#0B1F33]"
            >
              Republier maintenant
            </button>
          </div>
        )}

        {/* Couleurs et fond retirés (simplification demandée). */}

        {/* Section 1 · POLICES — sélecteur custom avec aperçu WYSIWYG de chaque police */}
        <div className="pt-4 mt-4 border-t border-slate-100">
          <div className="text-[9px] uppercase tracking-[0.22em] font-black text-[#FF9F1C] mb-2" data-testid="menu-section-polices">1 · Polices</div>
          <div className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500 mb-2">Typographies</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              ["titles", "Titres"], ["dish", "Noms de plats"],
              ["description", "Descriptions"], ["price", "Prix"],
            ].map(([k, label]) => (
              <label key={k} className="flex flex-col gap-1">
                <span className="text-[11px] text-slate-600">{label}</span>
                <MenuFontPicker
                  value={t.fonts[k] || "Inter"}
                  onChange={(f) => patch(`fonts.${k}`, f)}
                  options={THEME_FONTS}
                  testId={`menu-theme-font-${k}`}
                  label={label}
                />
              </label>
            ))}
          </div>
        </div>

        {/* Fond — retiré (simplification) */}

        {/* Section 2 · AFFICHAGE — 2 interrupteurs uniquement : photos + descriptions */}
        <div className="pt-3 border-t border-slate-100">
          <div className="text-[9px] uppercase tracking-[0.22em] font-black text-[#FF9F1C] mb-2" data-testid="menu-section-affichage">2 · Affichage</div>
          <div className="flex flex-wrap gap-2">
            {[
              ["show_photos", "Afficher les photos"],
              ["show_descriptions", "Afficher les descriptions"],
            ].map(([path, label]) => {
              const val = t[path];
              return (
                <button
                  key={path}
                  onClick={() => patch(path, !val)}
                  data-testid={`menu-theme-${path}`}
                  className={`text-[12px] px-3 py-1.5 rounded-md border transition-colors ${val ? "bg-[#DCEBFF] text-[#0B1F33] border-[#B4D1F9] font-semibold" : "bg-white text-slate-500 border-slate-200 line-through"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Ornements retirés (simplification demandée). */}
      </CardContent>
    </Card>
  );
}

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [header, setHeader] = useState({ title: "Le Petit Bistro", subtitle: "Menu du jour", footer: "" });
  const [translations, setTranslations] = useState(empty_header_tr());
  const [ds, setDs] = useState({
    active: false, title: "", price_ttc: "", description: "", allergens: [], accent: "amber", icon: "sparkles", translations: empty_ds_tr(),
  });
  const [slug, setSlug] = useState("");
  const [lang, setLang] = useState("fr");
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputRef = useRef();
  const [pendingUpload, setPendingUpload] = useState(null);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  // Historique thème pour Undo / Redo (Ctrl+Z / Ctrl+Y).
  const themeHistory = useRef([JSON.parse(JSON.stringify(DEFAULT_THEME))]);
  const themeCursor = useRef(0);
  const [themeCanUndo, setThemeCanUndo] = useState(false);
  const [themeCanRedo, setThemeCanRedo] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [publishing, setPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(null);
  const [translationsAllowed, setTranslationsAllowed] = useState(false);
  const [published, setPublished] = useState(false);
  const [translationsStale, setTranslationsStale] = useState(false);
  // Compteur pour forcer le rechargement de l'iframe d'aperçu après save.
  const [previewVersion, setPreviewVersion] = useState(0);
  const previewIframeRef = useRef();

  // Charge les polices dans le document de l'éditeur pour que le picker
  // WYSIWYG affiche chaque nom dans sa propre police (Chrome ignore
  // font-family sur les <option> natifs — d'où le custom picker).
  useEffect(() => { loadEditorFonts(THEME_FONTS); }, []);

  // Live preview : dès que le thème change, on envoie un postMessage à
  // l'iframe pour appliquer le nouveau thème sans attendre le save.
  // PublicMenu écoute `window.message` et met à jour son thème local si
  // `event.data.type === 'rentascan:preview-theme'`.
  useEffect(() => {
    const el = previewIframeRef.current;
    if (!el || !el.contentWindow) return;
    try { el.contentWindow.postMessage({ type: "rentascan:preview-theme", theme, translationsAllowed }, "*"); }
    catch { /* silent */ }
  }, [theme, translationsAllowed]);

  // Enregistre chaque modification de thème dans l'historique. Utilise
  // JSON.stringify pour éviter d'enregistrer un état identique.
  const setThemeWithHistory = (next) => {
    const nextTheme = typeof next === "function" ? next(theme) : next;
    if (JSON.stringify(nextTheme) === JSON.stringify(theme)) return;
    // Coupe l'historique après le curseur si on branche depuis un undo.
    themeHistory.current = themeHistory.current.slice(0, themeCursor.current + 1);
    themeHistory.current.push(JSON.parse(JSON.stringify(nextTheme)));
    if (themeHistory.current.length > 50) themeHistory.current.shift();
    themeCursor.current = themeHistory.current.length - 1;
    setThemeCanUndo(themeCursor.current > 0);
    setThemeCanRedo(false);
    setTheme(nextTheme);
    setDirty(true);
  };
  const themeUndo = () => {
    if (themeCursor.current <= 0) return;
    themeCursor.current -= 1;
    setTheme(JSON.parse(JSON.stringify(themeHistory.current[themeCursor.current])));
    setThemeCanUndo(themeCursor.current > 0);
    setThemeCanRedo(themeCursor.current < themeHistory.current.length - 1);
    setDirty(true);
  };
  const themeRedo = () => {
    if (themeCursor.current >= themeHistory.current.length - 1) return;
    themeCursor.current += 1;
    setTheme(JSON.parse(JSON.stringify(themeHistory.current[themeCursor.current])));
    setThemeCanUndo(themeCursor.current > 0);
    setThemeCanRedo(themeCursor.current < themeHistory.current.length - 1);
    setDirty(true);
  };
  // Raccourcis clavier Ctrl+Z / Ctrl+Y (ou Cmd sur Mac).
  useEffect(() => {
    const handler = (e) => {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      // Ignore quand on tape dans un champ éditable.
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (editable) return;
      if (e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); themeUndo(); }
      else if ((e.key.toLowerCase() === "y") || (e.key.toLowerCase() === "z" && e.shiftKey)) { e.preventDefault(); themeRedo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const d = await api.menu();
    setItems(d.items || []);
    setHeader({
      title: d.title || "Le Petit Bistro",
      subtitle: d.subtitle || "Menu du jour",
      footer: d.footer || "Prix nets, service compris · TVA 10 %",
    });
    setTranslations({ ...empty_header_tr(), ...(d.translations || {}) });
    const dds = d.daily_special || {};
    setDs({
      active: !!dds.active,
      title: dds.title || "",
      price_ttc: dds.price_ttc ?? "",
      description: dds.description || "",
      allergens: dds.allergens || [],
      accent: dds.accent || "amber",
      icon: dds.icon || "sparkles",
      translations: { ...empty_ds_tr(), ...(dds.translations || {}) },
    });
    setSlug(d.public_slug || "");
    // Thème visuel + flags de publication.
    setTheme({
      ...DEFAULT_THEME,
      ...(d.theme || {}),
      colors: { ...DEFAULT_THEME.colors, ...(d.theme?.colors || {}) },
      fonts: { ...DEFAULT_THEME.fonts, ...(d.theme?.fonts || {}) },
      background: { ...DEFAULT_THEME.background, ...(d.theme?.background || {}) },
      decorations: { ...DEFAULT_THEME.decorations, ...(d.theme?.decorations || {}) },
    });
    setTranslationsAllowed(!!d.translations_visible);
    setPublished(!!d.published_at);
    setTranslationsStale(!!d.translations_stale);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const markDirty = () => setDirty(true);

  const move = (idx, dir) => {
    const next = [...items];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    next.forEach((it, i) => (it.sort_order = i));
    setItems(next); markDirty();
  };
  const toggle = (idx) => {
    const next = [...items];
    next[idx] = { ...next[idx], active: !next[idx].active };
    setItems(next); markDirty();
  };
  const openEdit = (idx) => {
    const it = items[idx];
    const tr = { ...empty_item_tr(), ...(it.translations || {}) };
    setEditIdx(idx);
    setEditForm({
      display_name: it.display_name || "",
      description: it.description || "",
      price_override_ttc: it.price_override_ttc ?? "",
      tr,
    });
  };
  const saveEdit = async () => {
    const next = [...items];
    next[editIdx] = {
      ...next[editIdx],
      display_name: editForm.display_name || "",
      description: editForm.description || "",
      price_override_ttc: editForm.price_override_ttc === "" ? null : Number(editForm.price_override_ttc),
      translations: editForm.tr,
    };
    setItems(next);
    setEditIdx(null);
    // Feb 2026 — « Appliquer » applique ET enregistre en un seul geste.
    // On construit le payload à partir de `next` (setItems asynchrone) puis
    // on appelle directement l'API — évite un « dirty » persistant.
    try {
      const payload = {
        title: header.title, subtitle: header.subtitle, footer: header.footer,
        translations,
        daily_special: {
          ...ds,
          price_ttc: ds.price_ttc === "" ? null : Number(ds.price_ttc),
        },
        items: next.map((it) => ({
          recipe_id: it.recipe_id, sort_order: it.sort_order, active: it.active,
          display_name: it.display_name || "", description: it.description || "",
          price_override_ttc: it.price_override_ttc ?? null,
          translations: { ...empty_item_tr(), ...(it.translations || {}) },
        })),
        theme: theme,
      };
      await api.updateMenu(payload);
      setDirty(false);
      setPreviewVersion((v) => v + 1);
      toast.success("Modification enregistrée");
    } catch (e) {
      markDirty();
      toast.error("Enregistrement impossible");
    }
  };

  const save = async () => {
    const payload = {
      title: header.title, subtitle: header.subtitle, footer: header.footer,
      translations,
      daily_special: {
        ...ds,
        price_ttc: ds.price_ttc === "" ? null : Number(ds.price_ttc),
      },
      items: items.map((it) => ({
        recipe_id: it.recipe_id, sort_order: it.sort_order, active: it.active,
        display_name: it.display_name || "", description: it.description || "",
        price_override_ttc: it.price_override_ttc ?? null,
        translations: { ...empty_item_tr(), ...(it.translations || {}) },
      })),
      theme: theme,
    };
    await api.updateMenu(payload);
    setDirty(false);
    toast.success("Menu enregistré");
  };

  // Publie le menu : précharge les 7 langues côté serveur avec polling.
  // Cadenas Intégral géré côté client (message d'upsell) ET côté serveur
  // (route /menu/publish renvoie 403 translation_locked pour Essentiel/Pro).
  const publish = async () => {
    if (!translationsAllowed) {
      toast.error("Traduction en 7 langues disponible avec la formule Intégral", {
        description: "Publiez votre menu en français gratuitement via « Enregistrer ». Passez à Intégral pour publier automatiquement en 7 langues.",
        duration: 8000,
        action: {
          label: "Voir Intégral",
          onClick: () => { window.location.href = "/parametres"; },
        },
      });
      return;
    }
    if (dirty) { try { await save(); } catch { toast.error("Enregistrement impossible"); return; } }
    setPublishing(true);
    setPublishProgress(null);
    try {
      const { task_id, target_languages } = await api.publishMenu();
      setPublishProgress({ status: "queued", target_languages, done: [], failed: {}, current: null });
      // Poll jusqu'à completed / partial / failed
      const started = Date.now();
      while (Date.now() - started < 180000) {
        await new Promise((r) => setTimeout(r, 1800));
        const st = await api.getPublishTask(task_id);
        setPublishProgress(st);
        if (["completed", "partial", "failed"].includes(st.status)) {
          if (st.status === "completed") {
            toast.success("Les 7 langues sont prêtes · Menu publié", { description: "L'ancienne carte reste accessible jusqu'à la publication complète." });
            setPublished(true);
          } else if (st.status === "partial") {
            const badLangs = Object.keys(st.failed || {}).join(", ").toUpperCase();
            toast.warning("Publication partielle", { description: `Échec pour : ${badLangs}. Relancez uniquement ces langues plus tard.` });
          } else {
            toast.error("Impossible de publier le menu", { description: st.error || "Réessayez dans quelques minutes." });
          }
          break;
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || "Publication impossible");
      toast.error("Publication échouée", { description: String(msg) });
    } finally {
      setPublishing(false);
    }
  };

  const doAutoTranslate = async (target) => {
    if (dirty) await save();
    setTranslating(target);
    try {
      const d = await api.translateMenu(target);
      setItems(d.items || []);
      setTranslations({ ...empty_header_tr(), ...(d.translations || {}) });
      const dds = d.daily_special || {};
      setDs((prev) => ({ ...prev, translations: { ...empty_ds_tr(), ...(dds.translations || {}) } }));
      setLang(target);
      toast.success(`Menu traduit en ${target.toUpperCase()}`);
    } catch (e) {
      toast.error("Traduction RentaScan échouée");
    } finally { setTranslating(null); }
  };

  const pickPhoto = (recipeId) => { setPendingUpload(recipeId); setTimeout(() => fileInputRef.current?.click(), 0); };
  const removePhotoFor = async (recipeId) => {
    try { await api.deleteRecipePhoto(recipeId); toast.success("Photo supprimée"); await load(); }
    catch { toast.error("Suppression échouée"); }
  };
  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !pendingUpload) return;
    setUploadingFor(pendingUpload);
    try {
      await api.uploadRecipePhoto(pendingUpload, f);
      toast.success("Photo enregistrée");
      await load();
    } catch (err) { toast.error("Upload photo échoué"); }
    finally { setUploadingFor(null); setPendingUpload(null); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const priceOf = (it) => (it.price_override_ttc != null && it.price_override_ttc !== "" ? Number(it.price_override_ttc) : it.recipe?.selling_price_ttc);
  const nameForLang = (it, l) => l === "fr" ? (it.display_name?.trim() || it.recipe?.name || "") : (it.translations?.[l]?.display_name?.trim() || it.display_name?.trim() || it.recipe?.name || "");
  const descForLang = (it, l) => l === "fr" ? (it.description || it.recipe?.description || "") : (it.translations?.[l]?.description || it.description || it.recipe?.description || "");
  const headerForLang = (l) => l === "fr" ? header : {
    title: translations[l]?.title || header.title,
    subtitle: translations[l]?.subtitle || header.subtitle,
    footer: translations[l]?.footer || header.footer,
  };

  const publicUrl = useMemo(() => {
    if (!slug) return "";
    return `${window.location.origin}/carte/${slug}`;
  }, [slug]);
  const qrImgUrl = publicUrl ? api.menuQrUrl(publicUrl, 10) : "";

  const currentHeader = headerForLang(lang);
  const activeItems = items.filter((it) => it.active && it.recipe);
  const byCategory = activeItems.reduce((acc, it) => {
    const cat = it.recipe.category || "Autre";
    (acc[cat] = acc[cat] || []).push(it);
    return acc;
  }, {});
  const orderedCats = Object.keys(byCategory);

  if (loading) return <div className="text-slate-500">Chargement…</div>;

  // Use central helper to build absolute media URLs. Keeps behaviour but avoids
  // double /api regressions and centralises the rule.
  const buildPhotoUrl = (u) => {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    if (u.startsWith("/")) return `${API.replace(/\/api$/, "")}${u}`;
    return u;
  };
  const DsIcon = ICON_BY[ds.icon] || Sparkles;
  const dsTitleShown = lang === "fr" ? ds.title : (ds.translations?.[lang]?.title || ds.title);
  const dsDescShown = lang === "fr" ? ds.description : (ds.translations?.[lang]?.description || ds.description);
  const dsBand = ACCENT_BAND[ds.accent] || ACCENT_BAND.amber;

  return (
    <div className="space-y-6">
      <PageHero
        eyebrow="Éditeur & QR"
        title="Menu"
        subtitle="Personnalisez, ajoutez photos, traductions, QR code et impression."
        pattern="qr"
        action={
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="menu-save-btn"
              onClick={save}
              disabled={!dirty}
              className="gap-2 h-11 px-5 rounded-lg bg-[#FF9F1C] hover:bg-[#F58F06] text-[#0B1F33] font-bold border-0 disabled:opacity-50"
              title={dirty ? "Enregistrer les modifications" : "Aucune modification à enregistrer"}
            >
              <Save className="w-4 h-4" /> {dirty ? "Enregistrer" : "Enregistré"}
            </Button>
            <Button
              data-testid="menu-preview-btn"
              onClick={async () => {
                if (dirty) { await save(); }
                // Feb 2026 — L'« Aperçu client » ouvre la vraie page publique
                // (sans `?preview=1`) pour montrer l'expérience réelle du
                // client : sélecteur des 7 langues en haut, exactement ce que
                // verra un visiteur qui scanne le QR code du menu.
                if (publicUrl) window.open(publicUrl, "_blank", "noopener,noreferrer");
                else toast.error("Lien public indisponible");
              }}
              variant="outline"
              className="gap-2 no-print rounded-lg h-11 px-5 border-[#0B1F33] bg-white hover:bg-[#F4F5EF] text-[#0B1F33] font-semibold"
            >
              <ExternalLink className="w-4 h-4" /> Aperçu client
            </Button>
            <Button data-testid="menu-qr-btn" onClick={() => setQrOpen(true)} variant="outline" className="gap-2 no-print rounded-lg h-11 px-5 border-[#0B1F33] bg-white hover:bg-[#F4F5EF] text-[#0B1F33] font-semibold">
              <QrCode className="w-4 h-4" /> QR Menu
            </Button>
            <Button data-testid="menu-print-btn" onClick={() => window.print()} variant="outline" className="gap-2 no-print rounded-lg h-11 px-5 border-[#0B1F33] bg-white hover:bg-[#F4F5EF] text-[#0B1F33] font-semibold">
              <Printer className="w-4 h-4" /> Imprimer / PDF
            </Button>
          </div>
        }
      />

      {/* Layout 2 colonnes desktop : grand aperçu à gauche, panneau outils
          en sidebar sticky à droite. Sur tablette/mobile, l'aperçu remonte
          au-dessus du panneau (stack vertical). */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4 items-start">
        {/* Colonne aperçu — reste plus grande, toujours visible */}
        <div className="space-y-4 order-2 lg:order-1">
          {slug && (
            <Card className="card-soft border-0 no-print" data-testid="menu-live-preview-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-semibold text-slate-900 text-[13px]">Aperçu client · {previewDevice === "phone" ? "Téléphone" : previewDevice === "tablet" ? "Tablette" : previewDevice === "print" ? "Impression" : "Ordinateur"}</h3>
                  <button
                    type="button"
                    onClick={async () => { if (dirty) await save(); setPreviewVersion((v) => v + 1); }}
                    data-testid="menu-preview-refresh"
                    className="text-[11.5px] font-semibold text-[#0B1F33] hover:underline"
                  >
                    {dirty ? "Enregistrer & rafraîchir" : "Rafraîchir"}
                  </button>
                </div>
                <div className="w-full flex justify-center bg-[#F4F5EF] rounded-lg overflow-hidden border border-slate-200" style={{ padding: 12 }}>
                  <iframe
                    ref={previewIframeRef}
                    title="Aperçu menu"
                    data-testid="menu-preview-iframe"
                    onLoad={(e) => {
                      try { e.currentTarget.contentWindow?.postMessage({ type: "rentascan:preview-theme", theme, translationsAllowed }, "*"); } catch {}
                    }}
                    src={`${window.location.origin}/carte/${slug}?preview=1&v=${previewVersion}`}
                    style={{
                      width: previewDevice === "phone" ? 375 : previewDevice === "tablet" ? 768 : previewDevice === "print" ? 900 : "100%",
                      maxWidth: "100%",
                      height: previewDevice === "phone" ? 640 : previewDevice === "tablet" ? 720 : 820,
                      border: "0",
                      background: "#fff",
                      borderRadius: 8,
                      boxShadow: "0 4px 16px rgba(11,31,51,0.08)",
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Colonne panneau outils — sticky sur desktop, en-tête plié sur mobile */}
        <div className="order-1 lg:order-2 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto space-y-4">
          <ThemePanel
            theme={theme}
            setTheme={setThemeWithHistory}
            previewDevice={previewDevice}
            setPreviewDevice={setPreviewDevice}
            onPublish={publish}
            publishing={publishing}
            publishProgress={publishProgress}
            translationsAllowed={translationsAllowed}
            published={published}
            translationsStale={translationsStale}
            canUndo={themeCanUndo}
            canRedo={themeCanRedo}
            onUndo={themeUndo}
            onRedo={themeRedo}
          />
        </div>
      </div>

      {/* Fin du bloc 2 colonnes — les cards suivantes (header, recettes)
          reviennent en pleine largeur. */}

      {/* Header editor */}
      <Card className="card-soft border-0 no-print">
        <CardContent className="p-5 space-y-3">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            En-tête et pied de page
            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-0 font-normal">{LANGS.find((l) => l.code === lang)?.flag} {lang.toUpperCase()}</Badge>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {lang === "fr" ? (
              <>
                <div><Label>Titre</Label><Input data-testid="menu-title" value={header.title} onChange={(e) => { setHeader({ ...header, title: e.target.value }); markDirty(); }} /></div>
                <div><Label>Sous-titre</Label><Input data-testid="menu-subtitle" value={header.subtitle} onChange={(e) => { setHeader({ ...header, subtitle: e.target.value }); markDirty(); }} /></div>
                <div><Label>Pied de page</Label><Input data-testid="menu-footer" value={header.footer} onChange={(e) => { setHeader({ ...header, footer: e.target.value }); markDirty(); }} /></div>
              </>
            ) : (
              <>
                <div><Label>Title ({lang.toUpperCase()})</Label>
                  <Input placeholder={header.title} value={translations[lang]?.title || ""} onChange={(e) => { setTranslations({ ...translations, [lang]: { ...translations[lang], title: e.target.value } }); markDirty(); }} /></div>
                <div><Label>Subtitle ({lang.toUpperCase()})</Label>
                  <Input placeholder={header.subtitle} value={translations[lang]?.subtitle || ""} onChange={(e) => { setTranslations({ ...translations, [lang]: { ...translations[lang], subtitle: e.target.value } }); markDirty(); }} /></div>
                <div><Label>Footer ({lang.toUpperCase()})</Label>
                  <Input placeholder={header.footer} value={translations[lang]?.footer || ""} onChange={(e) => { setTranslations({ ...translations, [lang]: { ...translations[lang], footer: e.target.value } }); markDirty(); }} /></div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Editable list */}
      <div className="no-print">
        <Card className="card-soft border-0">
          <CardContent className="p-0">
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-slate-900">Plats du menu</h3>
              <p className="text-xs text-slate-500 mt-1">↑↓ réordonner · ✎ personnaliser & traductions · 📷 photo · œil afficher/masquer.</p>
            </div>
            <div className="divide-y divide-slate-100">
              {items.map((it, idx) => {
                if (!it.recipe) return null;
                const r = it.recipe;
                const nameFR = it.display_name?.trim() || r.name;
                const photo = buildPhotoUrl(r.photo_url);
                return (
                  <div key={it.recipe_id} data-testid={`menu-item-${it.recipe_id}`} className="flex items-center gap-3 p-3">
                    <div className="flex flex-col">
                      <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} className="h-6 w-6"><ArrowUp className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="h-6 w-6"><ArrowDown className="w-4 h-4" /></Button>
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0 border border-slate-200">
                      {photo ? <img src={photo} alt={nameFR} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" /> : <Camera className="w-5 h-5 text-slate-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-slate-500 uppercase tracking-[0.14em]">{r.category}</div>
                      <div className="font-medium text-slate-900 truncate">{nameForLang(it, lang)}</div>
                      {descForLang(it, lang) && <div className="text-xs text-slate-500 italic truncate">{descForLang(it, lang)}</div>}
                    </div>
                    <div className="text-right hidden sm:block">
                      <div className="font-mono-num font-semibold text-slate-900">{eur(priceOf(it))}</div>
                      <div className="text-[10px] text-slate-500 font-mono-num">Marge {pct(r.matter_margin_pct)}</div>
                    </div>
                    <Button size="sm" variant="ghost" data-testid={`menu-photo-${it.recipe_id}`} onClick={() => pickPhoto(r.id)} title="Photo" disabled={uploadingFor === r.id}>
                      {uploadingFor === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                    </Button>
                    {photo && (
                      <Button size="sm" variant="ghost" data-testid={`menu-photo-del-${it.recipe_id}`} onClick={() => removePhotoFor(r.id)} title="Supprimer la photo" className="text-rose-700 hover:bg-rose-50">
                        <span className="text-xs">✕</span>
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`menu-edit-${it.recipe_id}`} onClick={() => openEdit(idx)} title="Personnaliser"><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" data-testid={`menu-toggle-${it.recipe_id}`} variant={it.active ? "outline" : "ghost"} onClick={() => toggle(idx)} className={`${it.active ? "" : "text-slate-400"} rounded-full`} title={it.active ? "Affiché sur le menu" : "Masqué du menu"}>
                      {it.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      <span className="hidden sm:inline ml-1">{it.active ? "Affiché" : "Masqué"}</span>
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={onFileChange} data-testid="menu-photo-input" />

      {/* Printable menu */}
      <div className="menu-print-only">
        <div className="text-center border-b-2 border-slate-900 pb-4 mb-6">
          <div className="flex items-center justify-center gap-2">
            <BookOpen className="w-6 h-6 text-slate-900" />
            <h1 className="font-heading text-3xl font-bold text-slate-900">{currentHeader.title}</h1>
          </div>
          <p className="text-sm text-slate-600 mt-1">{currentHeader.subtitle}</p>
        </div>
        {orderedCats.length === 0 && <p className="text-center text-slate-500">Aucun plat actif dans le menu.</p>}
        {orderedCats.map((cat) => (
          <div key={cat} className="mb-6">
            <h2 className="font-heading text-xl font-bold text-slate-900 border-b border-slate-300 pb-1 mb-3 uppercase tracking-wider">{cat}</h2>
            <div className="space-y-3">
              {byCategory[cat].map((it) => {
                const photo = buildPhotoUrl(it.recipe.photo_url);
                const allergens = it.recipe?.allergens || [];
                return (
                  <div key={it.recipe_id} className="flex gap-3 items-start">
                    {photo && <img src={photo} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-16 h-16 rounded-md object-cover border border-slate-200 shrink-0" />}
                    <div className="flex-1">
                      <div className="flex justify-between items-baseline gap-3">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="text-slate-900 font-medium">{nameForLang(it, lang)}</span>
                          {allergens.map((code) => (
                            <span key={code} title={ALLERGEN_BY_CODE[code]?.label || code} className="inline-block text-[9px] font-mono-num font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200">{code}</span>
                          ))}
                        </div>
                        <div className="flex-1 border-b border-dotted border-slate-300 mb-1"></div>
                        <div className="font-mono-num font-semibold text-slate-900">{eur(priceOf(it))}</div>
                      </div>
                      {descForLang(it, lang) && <div className="text-xs text-slate-600 italic ml-1 mt-0.5">{descForLang(it, lang)}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {(() => {
          const usedCodes = Array.from(new Set(activeItems.flatMap((it) => it.recipe?.allergens || [])));
          if (usedCodes.length === 0) return null;
          return (
            <div className="mt-6 pt-4 border-t border-slate-300">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Allergènes (INCO 1169/2011)</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-[11px] text-slate-700">
                {usedCodes.map((code) => (<div key={code}><span className="font-mono-num font-semibold mr-1">{code}</span>{ALLERGEN_BY_CODE[code]?.label || code}</div>))}
              </div>
            </div>
          );
        })()}
        <div className="text-center text-xs text-slate-500 mt-4 pt-2">{currentHeader.footer}</div>
      </div>

      {/* Edit dialog */}
      <Dialog open={editIdx !== null} onOpenChange={(v) => !v && setEditIdx(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="menu-item-edit-dialog">
          <DialogHeader><DialogTitle>Personnaliser le plat</DialogTitle></DialogHeader>
          {editIdx !== null && items[editIdx] && editForm && (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">
                Recette : <strong>{items[editIdx].recipe?.name}</strong> · prix calculé <span className="font-mono-num">{eur(items[editIdx].recipe?.selling_price_ttc)}</span>
              </div>
              <Tabs defaultValue="fr">
                <TabsContent value="fr" className="space-y-3 mt-3">
                  <div>
                    <Label>Nom affiché (FR)</Label>
                    <Input data-testid="menu-item-display-name" placeholder={items[editIdx].recipe?.name} value={editForm.display_name} onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Description (FR)</Label>
                      <Button type="button" size="sm" variant="outline" data-testid="menu-item-generate-desc" disabled={generatingDesc}
                        onClick={async () => {
                          setGeneratingDesc(true);
                          try {
                            const r = await api.generateDescription(items[editIdx].recipe.id);
                            setEditForm((f) => ({ ...f, description: r.description }));
                            toast.success("Description générée par RentaScan");
                          } catch { toast.error("Génération RentaScan échouée"); }
                          finally { setGeneratingDesc(false); }
                        }}
                        className="gap-2 text-[#0B1F33] border-[#DCEBFF] hover:bg-[#DCEBFF] rounded-full">
                        {generatingDesc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        Générer avec RentaScan
                      </Button>
                    </div>
                    <Textarea data-testid="menu-item-description" rows={2} placeholder="Servi avec ses pommes sarladaises…" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                  </div>
                </TabsContent>
                {TR_CODES.map((code) => (
                  <TabsContent key={code} value={code} className="space-y-3 mt-3">
                    <div>
                      <Label>Display name ({code.toUpperCase()})</Label>
                      <Input data-testid={`menu-item-name-${code}`}
                        value={editForm.tr[code]?.display_name || ""}
                        onChange={(e) => setEditForm({ ...editForm, tr: { ...editForm.tr, [code]: { ...editForm.tr[code], display_name: e.target.value } } })} />
                    </div>
                    <div>
                      <Label>Description ({code.toUpperCase()})</Label>
                      <Textarea data-testid={`menu-item-desc-${code}`} rows={2}
                        value={editForm.tr[code]?.description || ""}
                        onChange={(e) => setEditForm({ ...editForm, tr: { ...editForm.tr, [code]: { ...editForm.tr[code], description: e.target.value } } })} />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <div>
                <Label>Prix carte TTC (laisser vide pour le prix calculé)</Label>
                <Input data-testid="menu-item-price-override" type="number" step="0.10"
                  placeholder={String(items[editIdx].recipe?.selling_price_ttc || "")}
                  value={editForm.price_override_ttc}
                  onChange={(e) => setEditForm({ ...editForm, price_override_ttc: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIdx(null)}>Annuler</Button>
            <Button data-testid="menu-item-save" onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">Appliquer et enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-md" data-testid="menu-qr-dialog">
          <DialogHeader><DialogTitle>QR Menu · toujours à jour</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Vos clients scannent, choisissent leur langue et lisent la carte mise à jour en temps réel — aucune impression obsolète.
            </p>
            {qrImgUrl && (
              <div className="flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-slate-200">
                <img data-testid="menu-qr-image" src={qrImgUrl} alt="QR menu" className="w-56 h-56" />
                <div className="text-[11px] text-slate-500 font-mono-num break-all text-center">{publicUrl}</div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="menu-qr-copy" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Lien copié"); }} className="gap-2 rounded-full">
                <Copy className="w-4 h-4" /> Copier le lien
              </Button>
              <a href={qrImgUrl} download="qr-menu.png" data-testid="menu-qr-download">
                <Button size="sm" variant="outline" className="gap-2 rounded-full">
                  <QrCode className="w-4 h-4" /> Télécharger le PNG
                </Button>
              </a>
              <Button size="sm" variant="outline" data-testid="menu-qr-regenerate"
                onClick={async () => { const r = await api.regenerateMenuSlug(); setSlug(r.public_slug); toast.success("Nouveau lien généré"); }}
                className="gap-2 rounded-full">
                <RefreshCw className="w-4 h-4" /> Régénérer le lien
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQrOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
